/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { identityProviderConnectionsCreate } from "../funcs/identityProviderConnectionsCreate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaIdentityProviderConnection } from "../models/components/oktaidentityproviderconnection.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateIdentityProviderConnectionRequest,
  CreateIdentityProviderConnectionSecurity,
} from "../models/operations/createidentityproviderconnection.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateIdentityProviderConnectionMutationVariables = {
  request: CreateIdentityProviderConnectionRequest;
  security?: CreateIdentityProviderConnectionSecurity | undefined;
  options?: RequestOptions;
};

export type CreateIdentityProviderConnectionMutationData =
  OktaIdentityProviderConnection;

export type CreateIdentityProviderConnectionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * create identityProviderConnections
 *
 * @remarks
 * Create the organization's Okta connection. Discovers the org's authorization server, provisions a signing key and JWKS URL, and returns the console checklist. Requires org:admin and the okta-connections rollout. One live connection per organization; creation is rate limited.
 */
export function useCreateIdentityProviderConnectionMutation(
  options?: MutationHookOptions<
    CreateIdentityProviderConnectionMutationData,
    CreateIdentityProviderConnectionMutationError,
    CreateIdentityProviderConnectionMutationVariables
  >,
): UseMutationResult<
  CreateIdentityProviderConnectionMutationData,
  CreateIdentityProviderConnectionMutationError,
  CreateIdentityProviderConnectionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateIdentityProviderConnectionMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateIdentityProviderConnection(): MutationKey {
  return ["@gram/client", "identityProviderConnections", "create"];
}

export function buildCreateIdentityProviderConnectionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateIdentityProviderConnectionMutationVariables,
  ) => Promise<CreateIdentityProviderConnectionMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateIdentityProviderConnection(),
    mutationFn: function createIdentityProviderConnectionMutationFn({
      request,
      security,
      options,
    }): Promise<CreateIdentityProviderConnectionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(identityProviderConnectionsCreate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
